/**
 * Utility functions for handling school years
 */

/**
 * Get the current school year in YYYY-YYYY format
 * School year starts in August and ends in July of the following year
 * @returns {string} Current school year in format "2024-2025"
 */
export const getCurrentSchoolYear = () => {
  const currentDate = new Date();
  const currentYear = currentDate.getFullYear();
  const currentMonth = currentDate.getMonth(); // 0-based, so 0 = January

  // If it's before August (month 7), we're in the second half of the school year
  if (currentMonth < 7) {
    return `${currentYear - 1}-${currentYear}`;
  } else {
    return `${currentYear}-${currentYear + 1}`;
  }
};

/**
 * Validate school year format
 * @param {string} schoolYear - School year to validate
 * @returns {boolean} True if valid format (YYYY-YYYY)
 */
export const isValidSchoolYear = (schoolYear) => {
  if (!schoolYear || typeof schoolYear !== "string") {
    return false;
  }

  const match = schoolYear.match(/^(\d{4})-(\d{4})$/);
  if (!match) {
    return false;
  }

  const startYear = parseInt(match[1], 10);
  const endYear = parseInt(match[2], 10);

  // Validate that the end year is consecutive to start year
  return endYear === startYear + 1 && startYear >= 1990 && startYear <= 2050;
};

/**
 * Convert a full year to school year format
 * @param {number} year - Full year (e.g., 2024)
 * @returns {string} School year in format "2024-2025"
 */
export const yearToSchoolYear = (year) => {
  if (!year || typeof year !== "number") {
    return getCurrentSchoolYear();
  }

  return `${year}-${year + 1}`;
};

/**
 * Parse school year string to get start and end years
 * @param {string} schoolYear - School year in format "2024-2025"
 * @returns {object} Object with startYear and endYear properties, or null if invalid
 */
export const parseSchoolYear = (schoolYear) => {
  if (!isValidSchoolYear(schoolYear)) {
    return null;
  }

  const [startYear, endYear] = schoolYear.split("-");
  const startYearNum = parseInt(startYear, 10);
  const endYearNum = parseInt(endYear, 10);

  return {
    startYear: startYearNum,
    endYear: endYearNum,
  };
};

/**
 * Normalize a stored Course_School_Year for display/editing. Most records
 * hold "2024-2025", but older ones hold a bare number (2024 = 2024-2025).
 * Missing or unparseable values return "" — never a guessed default.
 * @param {string|number} value - Stored school year
 * @returns {string} School year in "YYYY-YYYY" format, or ""
 */
export const normalizeSchoolYear = (value) => {
  if (value === null || value === undefined || value === "") return "";
  if (typeof value === "number") {
    return Number.isFinite(value) ? yearToSchoolYear(value) : "";
  }
  const text = String(value).trim();
  return /^\d{4}$/.test(text) ? yearToSchoolYear(Number(text)) : text;
};
